import React, { useState } from "react";

import axiosRestaurantInstance from "../restaurant/AxiosRestaurantInstance";
import { getAuthHeaders } from "../organization/organizationStorage";
import { RESTAURANT_API_PATHS } from "../constants/apiPaths";
import {
  formatAadharDisplay,
  formatAadharDisplayOrFallback,
  sanitizeAadharInput,
  sanitizeMobileInput,
  validateAadharNumber,
  validateMobileNumber,
} from "../restaurant/formatDisplay";
import RootAdminUserPicker from "./RootAdminUserPicker";
import "./EatsAdminCreatePage.css";

const EMPTY_FORM = {
  firstName: "",
  lastName: "",
  mobile: "",
  email: "",
  aadharNo: "",
  password: "",
};

const RootAdminAssignPanel = ({
  restaurantId,
  restaurantName = "",
  createdById,
  title,
  onSuccess,
}) => {
  const [mode, setMode] = useState("existing");
  const [form, setForm] = useState(EMPTY_FORM);
  const [selectedUser, setSelectedUser] = useState(null);
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setForm(EMPTY_FORM);
    setSelectedUser(null);
    setMode("existing");
  };

  const handleSelectExisting = (user) => {
    setSelectedUser(user);
    setMode("existing");
    const nameParts = String(user?.name || "").trim().split(/\s+/);
    setForm({
      ...EMPTY_FORM,
      firstName: user?.firstName || nameParts[0] || "",
      lastName: user?.lastName || nameParts.slice(1).join(" ") || "",
      mobile: user?.mobile || user?.contactNumber || "",
      email: user?.email || "",
      aadharNo: user?.aadharNo || "",
      password: "",
    });
  };

  const handleCreateNew = () => {
    setSelectedUser(null);
    setForm(EMPTY_FORM);
    setMode("new");
  };

  const handleBackToSearch = () => {
    setSelectedUser(null);
    setForm(EMPTY_FORM);
    setMode("existing");
  };

  const handleSave = async () => {
    if (!restaurantId) {
      alert("Please create a restaurant first.");
      return;
    }
    if (!createdById) {
      alert("Current user is missing. Please sign in again.");
      return;
    }

    const assigningExisting = mode === "existing" && selectedUser?.userId;
    const { firstName, lastName, mobile, email, aadharNo, password } = form;

    if (assigningExisting) {
      setSaving(true);
      try {
        const response = await axiosRestaurantInstance.post(
          RESTAURANT_API_PATHS.restaurantsUsers,
          {
            userId: selectedUser.userId,
            roleNames: ["ROOT_ADMIN"],
            restaurantId,
            restaurantName,
            createdById,
            status: "ACTIVE",
          },
          { headers: getAuthHeaders() }
        );
        alert(response?.data?.message || "Owner assigned successfully");
        reset();
        onSuccess?.({ assigned: true, restaurantId });
      } catch (error) {
        alert(error?.response?.data?.message || "Failed to assign owner");
      } finally {
        setSaving(false);
      }
      return;
    }

    if (!firstName || !lastName || !mobile || !aadharNo?.trim() || !password) {
      alert("Please fill first name, last name, personal mobile, aadhar number and password.");
      return;
    }
    const mobileError = validateMobileNumber(mobile, { required: true, label: "Personal Mobile" });
    if (mobileError) {
      alert(mobileError);
      return;
    }
    const aadharError = validateAadharNumber(aadharNo, { required: true, label: "Aadhar number" });
    if (aadharError) {
      alert(aadharError);
      return;
    }

    setSaving(true);
    try {
      const response = await axiosRestaurantInstance.post(
        RESTAURANT_API_PATHS.restaurantsUsers,
        {
          firstName,
          lastName,
          mobile,
          email,
          aadharNo: sanitizeAadharInput(aadharNo),
          password,
          roleNames: ["ROOT_ADMIN"],
          restaurantId,
          restaurantName,
          createdById,
          status: "ACTIVE",
        },
        { headers: getAuthHeaders() }
      );
      alert(response?.data?.message || "Owner created successfully");
      reset();
      onSuccess?.({ created: true, restaurantId });
    } catch (error) {
      alert(error?.response?.data?.message || "Failed to create owner");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="eats-admin-root-admin">
      {title ? <h3 className="eats-admin-root-admin__title">{title}</h3> : null}

      {mode === "existing" ? (
        <RootAdminUserPicker
          axiosInstance={axiosRestaurantInstance}
          apiPath={RESTAURANT_API_PATHS.restaurantRootAdminUsers}
          selectedUserId={selectedUser?.userId}
          onSelect={handleSelectExisting}
          onClear={handleBackToSearch}
          autoLoad={false}
          requireQuery
          typeFilter="RST"
          toolbarRight={
            <button
              type="button"
              className="eats-admin-btn eats-admin-btn--create"
              onClick={handleCreateNew}
            >
              Create New
            </button>
          }
        />
      ) : (
        <div className="eats-admin-root-admin__create-bar">
          <button
            type="button"
            className="eats-admin-root-admin__back-search"
            onClick={handleBackToSearch}
          >
            Search existing
          </button>
          <span className="eats-admin-root-admin__create-label">Create New Owner</span>
        </div>
      )}

      {mode === "new" ? (
        <div className="eats-admin-form-grid">
          <input
            placeholder="First Name *"
            value={form.firstName}
            onChange={(e) => setForm((p) => ({ ...p, firstName: e.target.value }))}
          />
          <input
            placeholder="Last Name *"
            value={form.lastName}
            onChange={(e) => setForm((p) => ({ ...p, lastName: e.target.value }))}
          />
          <input
            placeholder="Personal Mobile *"
            value={form.mobile}
            onChange={(e) => setForm((p) => ({ ...p, mobile: sanitizeMobileInput(e.target.value) }))}
            maxLength={10}
            inputMode="numeric"
          />
          <input
            placeholder="Email"
            value={form.email}
            onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
          />
          <input
            placeholder="Aadhar Number *"
            value={formatAadharDisplay(form.aadharNo)}
            onChange={(e) => setForm((p) => ({ ...p, aadharNo: sanitizeAadharInput(e.target.value) }))}
            maxLength={14}
            inputMode="numeric"
          />
          <input
            type="password"
            placeholder="Password *"
            value={form.password}
            onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
          />
        </div>
      ) : selectedUser ? (
        <div className="eats-admin-selected-user">
          Selected:{" "}
          <strong>
            {selectedUser.name || `${form.firstName} ${form.lastName}`.trim()}
          </strong>
          {" · "}Aadhar:{" "}
          <strong>{formatAadharDisplayOrFallback(selectedUser.aadharNo || form.aadharNo)}</strong>
          {" · "}Personal Mobile:{" "}
          <strong>{selectedUser.mobile || form.mobile || "—"}</strong>
        </div>
      ) : (
        <div className="eats-admin-hint">
          Search by name or Aadhar number and select a user, or use Create New.
        </div>
      )}

      <div className="eats-admin-actions">
        <button
          type="button"
          className="eats-admin-btn eats-admin-btn--primary"
          onClick={handleSave}
          disabled={saving || (mode === "existing" && !selectedUser?.userId)}
        >
          {saving ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
};

export default RootAdminAssignPanel;
